import { useTranslation } from "react-i18next";
import type { Currency, Locale, Package, Product } from "@nh/shared";
import { ShopError } from "@/features/shop/components/ShopError";
import { WholesaleUpsellNote } from "./WholesaleUpsellNote";
import { PackageGrid } from "./PackageGrid";
import { PackageGridSkeleton } from "./PackageGridSkeleton";
import { PackagesEmpty } from "./PackagesEmpty";
import { PackagesMeta } from "./PackagesMeta";

export interface PackagesPanelProps {
  /** Los paquetes de ESTA categoría, ya repartidos y ordenados. */
  packages: readonly Package[];
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  retrying: boolean;
  currency: Currency;
  locale: Locale;
  numberLocale: string;
  productsById: Map<string, Product> | null;
}

/**
 * El panel de una categoría: el aviso que le corresponda, la fila de
 * datos y —según cómo venga la petición— esqueletos, aviso de error,
 * estado vacío o la rejilla.
 *
 * ── Los cuatro estados se ven distintos ──────────────────────────────
 * Cargando → cajas con la forma de las tarjetas, para que nada salte
 * cuando lleguen los datos. Error → un aviso con su código y un botón de
 * reintentar. Vacío → un estado que NO parece un error, porque no lo es.
 * Nunca una pantalla en blanco. Es la misma escalera que la tienda, y a
 * propósito: son la misma persona mirando dos pantallas del mismo sitio.
 *
 * ── El aviso de error se REUSA de la tienda ──────────────────────────
 * `ShopError` acepta un titular distinto justo para esto: el fallo es
 * idéntico (el servidor no responde) y la salida también (reintentar).
 * Un segundo componente con el texto cambiado sería un gemelo que hay que
 * mantener dos veces. Deuda anotada, la misma que anotó franquicias: este
 * aviso es del SITIO y no de la tienda, y le toca subir a un sitio neutral.
 *
 * ── Es el `tabpanel` ─────────────────────────────────────────────────
 * `role="tabpanel"` con `aria-labelledby` a su pestaña, y `tabIndex={0}`
 * porque el panel contiene contenido desplazable: sin él, quien llega con
 * el teclado a la pestaña no tiene forma de entrar al panel sin recorrer
 * la otra pestaña primero.
 */
export function PackagesPanel({
  packages,
  isPending,
  isError,
  error,
  onRetry,
  retrying,
  currency,
  locale,
  numberLocale,
  productsById,
}: PackagesPanelProps) {
  const { t } = useTranslation();

  return (
    <div
      role="region"
      aria-label={t("Paquetes")}
      className="grid gap-6 focus:outline-none"
    >
      {/* Siempre en la pestaña PERSONAL: el llamado a mayorista. */}
      <WholesaleUpsellNote />

      {!isPending && !isError && (
        <PackagesMeta count={packages.length} />
      )}

      {isPending ? (
        <PackageGridSkeleton />
      ) : isError ? (
        <ShopError
          error={error}
          onRetry={onRetry}
          retrying={retrying}
          title={t("No pudimos cargar los paquetes")}
        />
      ) : packages.length === 0 ? (
        <PackagesEmpty />
      ) : (
        <PackageGrid
          packages={packages}
          currency={currency}
          locale={locale}
          numberLocale={numberLocale}
          productsById={productsById}
        />
      )}
    </div>
  );
}
